import { api, getJson, sendJson } from '@/lib/api';
import type { DocumentV3 } from '@docgen/schema';

/**
 * The proposal editor's data client, on bcecloud's `/api/v3/proposals` (gated
 * by the `generate_proposals` permission). A proposal offers one or more
 * OPTIONS (packages); each option holds programs (participants × per-participant
 * price) that sum to the option's price, minus its own discounts = its net.
 * The `DocumentV3` body is a projection the server rewrites from the pricing.
 */

export type DiscountKind = 'percent' | 'fixed';

/** One program within an option. */
export interface PricingProgram {
  readonly id: string;
  readonly programId?: string;
  readonly programName: string;
  readonly participants: number;
  readonly unitPrice: number;
  /** When true, unitPrice is a flat program fee (total), not a per-participant rate. */
  readonly flat?: boolean;
  /** Base + overage: base fee covering `includedParticipants`, then `unitPrice` per person after. */
  readonly basePrice?: number;
  readonly includedParticipants?: number;
  readonly note?: string;
}

export interface PricingDiscount {
  readonly id: string;
  readonly kind: DiscountKind;
  readonly value: number;
  readonly label?: string;
  readonly reason?: string;
}

/** One option (package): a bundle of programs the client can choose. */
export interface PricingOption {
  readonly id: string;
  readonly name: string;
  readonly programs: readonly PricingProgram[];
  readonly discounts: readonly PricingDiscount[];
  readonly note?: string;
}

export interface ProposalPricing {
  readonly currency: 'USD';
  readonly validUntil: string | null;
  readonly options: readonly PricingOption[];
}

export interface PricedProgram extends PricingProgram {
  readonly total: number;
}

export interface AppliedDiscount extends PricingDiscount {
  readonly label: string;
  readonly amount: number;
}

/** Everything one option prints. */
export interface OptionTotals {
  readonly id: string;
  readonly name: string;
  readonly programs: readonly PricedProgram[];
  readonly subtotal: number;
  readonly discountTotal: number;
  readonly net: number;
  readonly participants: number;
  readonly perPerson: number | null;
  readonly percentOff: number;
  readonly fixedOff: number;
  readonly discounts: readonly AppliedDiscount[];
  readonly warnings: readonly string[];
}

export interface PricingTotals {
  readonly options: readonly OptionTotals[];
  /** With exactly one option, its net; otherwise null. */
  readonly net: number | null;
  readonly participants: number;
  readonly warnings: readonly string[];
}

/** What the proposal remembers about its Salesforce Opportunity. */
export interface ProposalOpportunity {
  readonly id: string;
  readonly name?: string;
  readonly accountName?: string;
  readonly stageName?: string;
  readonly eventDate?: string;
  readonly ownerName?: string;
}

export type ProposalStatus = 'draft' | 'sent';

/** A proposal as the list needs it. */
export interface ProposalSummary {
  readonly id: string;
  readonly name: string;
  readonly status: ProposalStatus;
  readonly pricing: ProposalPricing;
  readonly totals: PricingTotals;
  readonly opportunityId?: string;
  readonly opportunity?: ProposalOpportunity;
  readonly updatedAt?: string;
  readonly createdAt?: string;
}

/** The proposal record the API returns. */
export interface ProposalRecord extends ProposalSummary {
  readonly document: DocumentV3;
}

/** Optional record links set when a proposal is created from a Salesforce/portal context. */
export interface ProposalLinks {
  readonly opportunityId?: string;
  readonly eventId?: string;
  readonly campaignId?: string;
}

export interface CreateProposalInput extends ProposalLinks {
  readonly name?: string;
  readonly document?: DocumentV3;
}

/** An opportunity as the picker shows it. `id` is the Salesforce id. */
export interface PickerOpportunity {
  readonly id: string;
  readonly name: string;
  readonly accountName: string;
  readonly stageName: string;
  readonly eventDate: string | null;
  readonly eventCity: string;
  readonly eventState: string;
  readonly participants: number | null;
  readonly totalCost: number | null;
  readonly programName: string;
  readonly invoiceNumber: string;
  readonly ownerName: string;
}

export interface PickerResult {
  readonly items: readonly PickerOpportunity[];
  readonly unresolvedOwner: boolean;
}

export interface PricingView {
  readonly pricing: ProposalPricing;
  readonly totals: PricingTotals;
}

/** A pricing mutation's result: the saved proposal (re-projected document included) and totals. */
export interface PricingResult extends PricingView {
  readonly proposal: ProposalRecord;
}

export interface AddProgramInput {
  readonly programId?: string;
  readonly programName?: string;
  readonly participants: number;
  readonly unitPrice: number;
  readonly flat?: boolean;
  readonly note?: string;
}

export interface UpdateProgramInput {
  readonly participants?: number;
  readonly unitPrice?: number;
  /** Switch between a flat fee (true) and a per-participant rate (false). */
  readonly flat?: boolean;
  /** `null` clears the note. */
  readonly note?: string | null;
}

export interface AddDiscountInput {
  readonly kind: DiscountKind;
  readonly value: number;
  readonly label?: string;
  readonly reason?: string;
}

/** Loads a proposal with its document, pricing and totals. */
export async function getProposal(id: string): Promise<ProposalRecord> {
  return getJson<ProposalRecord>(`/proposals/${id}`);
}

/** Loads a proposal's document body by proposal id. */
export async function getDocument(id: string): Promise<DocumentV3> {
  return (await getProposal(id)).document;
}

/** The document for the print view — the one route a scoped print token can open. */
export async function getPrintDocument(id: string): Promise<DocumentV3> {
  return (await getJson<ProposalRecord>(`/proposals/${id}/print`)).document;
}

/**
 * Saves a proposal's document body (autosave). The server re-projects pricing
 * and rebuilds the deal-derived introduction and labels, and returns the result.
 */
export async function saveDocument(id: string, doc: DocumentV3): Promise<ProposalRecord> {
  return sendJson<ProposalRecord>('put', `/proposals/${id}`, { document: doc });
}

/** Rendering a PDF runs headless Chromium on the server: allow it well past the API's default timeout. */
const PDF_TIMEOUT_MS = 120_000;
const FALLBACK_PDF_NAME = 'Proposal.pdf';

/** A rendered proposal PDF and the name the server gave it. */
export interface ProposalPdf {
  readonly blob: Blob;
  readonly fileName: string;
}

/** The filename from a `Content-Disposition` header, else a plain default. */
export function pdfFileName(disposition: unknown): string {
  if (typeof disposition !== 'string') return FALLBACK_PDF_NAME;
  const match = /filename="([^"]+)"/.exec(disposition);
  return match?.[1] ?? FALLBACK_PDF_NAME;
}

/**
 * The proposal rendered as a PDF by the server (refused, like the print view,
 * while the proposal has export issues). For the Salesforce embed, which
 * cannot hand its in-memory session to a print-view tab.
 */
export async function getProposalPdf(id: string): Promise<ProposalPdf> {
  const response = await api.get<Blob>(`/proposals/${id}/pdf`, { responseType: 'blob', timeout: PDF_TIMEOUT_MS });
  return { blob: response.data, fileName: pdfFileName(response.headers['content-disposition']) };
}

/** Kinds of export problem, as the API names them. */
export const READINESS_ISSUE = {
  missing: 'missing',
  sample: 'sample',
} as const;

/** One thing that stops the proposal being exported. */
export interface ReadinessIssue {
  readonly kind: (typeof READINESS_ISSUE)[keyof typeof READINESS_ISSUE];
  readonly subject: string;
  readonly message: string;
}

export interface ProposalReadiness {
  readonly ready: boolean;
  readonly issues: readonly ReadinessIssue[];
}

/** What stops the proposal being exported as a PDF (sample text, empty required fields). */
export async function getReadiness(id: string): Promise<ProposalReadiness> {
  return getJson<ProposalReadiness>(`/proposals/${id}/readiness`);
}

/** The caller's proposals, newest first; optionally only those linked to one Opportunity. */
export async function listProposals(opportunityId?: string): Promise<ProposalSummary[]> {
  return getJson<ProposalSummary[]>('/proposals', opportunityId ? { opportunityId } : undefined);
}

/** Creates a proposal — from an opportunity the caller may use, a template, or nothing. */
export async function createProposal(input: CreateProposalInput): Promise<ProposalRecord> {
  return sendJson<ProposalRecord>('post', '/proposals', input);
}

/** Creates a proposal from a document and returns its new id. */
export async function createDocument(name: string, document: DocumentV3, links: ProposalLinks = {}): Promise<string> {
  return (await createProposal({ name, document, ...links })).id;
}

/** Renames a proposal. */
export async function renameDocument(id: string, name: string): Promise<void> {
  await sendJson('patch', `/proposals/${id}`, { name });
}

/** Deletes a proposal (the server keeps it, hidden, for recovery). */
export async function deleteDocument(id: string): Promise<void> {
  await sendJson('delete', `/proposals/${id}`);
}

/** Resets a proposal's pages and theme to the BCE template, keeping its pricing and salesperson. */
export async function resetProposal(id: string): Promise<ProposalRecord> {
  return sendJson<ProposalRecord>('post', `/proposals/${id}/reset`);
}

/** Open opportunities the caller may build a proposal on. */
export async function searchOpportunities(q: string, signal?: AbortSignal): Promise<PickerResult> {
  return getJson<PickerResult>('/proposals/opportunities', q.trim() ? { q: q.trim() } : undefined, signal);
}

/** The proposal's pricing and totals. */
export async function getPricing(id: string): Promise<PricingView> {
  return getJson<PricingView>(`/proposals/${id}/pricing`);
}

/** Sets (or clears, with null) the date the pricing holds until. */
export async function setValidity(id: string, validUntil: string | null): Promise<PricingResult> {
  return sendJson<PricingResult>('patch', `/proposals/${id}/pricing`, { validUntil });
}

/** Adds an option (package). */
export async function addOption(id: string, name?: string): Promise<PricingResult> {
  return sendJson<PricingResult>('post', `/proposals/${id}/options`, name ? { name } : {});
}

/** Renames an option. */
export async function renameOption(id: string, optionId: string, name: string): Promise<PricingResult> {
  return sendJson<PricingResult>('patch', `/proposals/${id}/options/${optionId}`, { name });
}

/** Removes an option and everything in it. */
export async function removeOption(id: string, optionId: string): Promise<PricingResult> {
  return sendJson<PricingResult>('delete', `/proposals/${id}/options/${optionId}`);
}

/** Adds a program to an option. */
export async function addProgram(id: string, optionId: string, input: AddProgramInput): Promise<PricingResult> {
  return sendJson<PricingResult>('post', `/proposals/${id}/options/${optionId}/programs`, input);
}

/** Changes a program's headcount, price or note. */
export async function updateProgram(
  id: string,
  optionId: string,
  programId: string,
  patch: UpdateProgramInput,
): Promise<PricingResult> {
  return sendJson<PricingResult>('patch', `/proposals/${id}/options/${optionId}/programs/${programId}`, patch);
}

/** Removes a program from an option. */
export async function removeProgram(id: string, optionId: string, programId: string): Promise<PricingResult> {
  return sendJson<PricingResult>('delete', `/proposals/${id}/options/${optionId}/programs/${programId}`);
}

/** Adds a discount to an option. */
export async function addDiscount(id: string, optionId: string, input: AddDiscountInput): Promise<PricingResult> {
  return sendJson<PricingResult>('post', `/proposals/${id}/options/${optionId}/discounts`, input);
}

/** Removes a discount from an option. */
export async function removeDiscount(id: string, optionId: string, discountId: string): Promise<PricingResult> {
  return sendJson<PricingResult>('delete', `/proposals/${id}/options/${optionId}/discounts/${discountId}`);
}
